<template>
  <DefaultAuthLayout>
    <!-- Top Action Slot -->
    <template #topAction>
      <span class="text-gray mr-3 lg:inline">Já possui conta?</span>
      <router-link to="/login" class="border border-stroke px-3 py-1 text-sm text-body transition-colors hover:bg-gray-2 lg:px-6 lg:py-2 lg:text-base">
        Entrar
      </router-link>
    </template>

    <!-- Header Slot -->
    <template #header>
      <h2 class="mb-2 text-2xl font-bold text-black lg:text-3xl">Criar Conta</h2>
      <p class="text-sm text-body lg:text-base">Preencha os dados abaixo para criar sua conta</p>
    </template>

    <!-- Form Slot -->
    <template #form>
      <form @submit.prevent="handleSubmit" class="space-y-3 lg:space-y-4">
        <!-- Nome Field -->
        <div class="mb-3 lg:mb-4">
          <label class="mb-2 block text-xs font-medium uppercase tracking-wide text-black lg:text-sm"> Nome Completo </label>
          <div class="relative">
            <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 lg:pl-4">
              <IcUser />
            </div>
            <input
              type="text"
              name="name"
              v-model="form.name"
              placeholder="Digite seu nome completo"
              class="w-full border border-stroke bg-transparent py-2.5 pl-10 pr-3 text-sm text-black outline-none transition-colors focus:border-blue focus:ring-2 focus:ring-blue lg:py-3 lg:pl-12 lg:pr-4 lg:text-base" />
          </div>
          <p v-if="errors.name" class="mt-1 text-xs text-danger">{{ errors.name }}</p>
        </div>

        <!-- Email Field -->
        <div class="mb-3 lg:mb-4">
          <label class="mb-2 block text-xs font-medium uppercase tracking-wide text-black lg:text-sm"> Email </label>
          <div class="relative">
            <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 lg:pl-4">
              <IcEmail />
            </div>
            <input
              type="email"
              name="email"
              v-model="form.email"
              placeholder="Digite seu email"
              class="w-full border border-stroke bg-transparent py-2.5 pl-10 pr-3 text-sm text-black outline-none transition-colors focus:border-blue focus:ring-2 focus:ring-blue lg:py-3 lg:pl-12 lg:pr-4 lg:text-base" />
          </div>
          <p v-if="errors.email" class="mt-1 text-xs text-danger">{{ errors.email }}</p>
        </div>

        <!-- Empresa Field -->
        <div class="mb-3 lg:mb-4">
          <label class="mb-2 block text-xs font-medium uppercase tracking-wide text-black lg:text-sm"> Empresa </label>
          <div class="relative">
            <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 lg:pl-4">
              <IcBuilding />
            </div>
            <input
              type="text"
              name="company"
              v-model="form.company"
              placeholder="Digite o nome da empresa"
              class="w-full border border-stroke bg-transparent py-2.5 pl-10 pr-3 text-sm text-black outline-none transition-colors focus:border-blue focus:ring-2 focus:ring-blue lg:py-3 lg:pl-12 lg:pr-4 lg:text-base" />
          </div>
          <p v-if="errors.company" class="mt-1 text-xs text-danger">{{ errors.company }}</p>
        </div>

        <!-- Password Field -->
        <div class="mb-3 lg:mb-4">
          <label class="mb-2 block text-xs font-medium uppercase tracking-wide text-black lg:text-sm"> Senha </label>
          <div class="relative">
            <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 lg:pl-4">
              <IcLock />
            </div>
            <input
              :type="showPassword ? 'text' : 'password'"
              name="password"
              v-model="form.password"
              placeholder="Digite uma senha segura"
              class="w-full border border-stroke bg-transparent py-2.5 pl-10 pr-10 text-sm text-black outline-none transition-colors focus:border-blue focus:ring-2 focus:ring-blue lg:py-3 lg:pl-12 lg:pr-12 lg:text-base" />
            <button type="button" @click="showPassword = !showPassword" class="absolute inset-y-0 right-0 flex items-center pr-3 lg:pr-4">
              <IcEyeOff v-if="showPassword" />
              <IcEye v-else />
            </button>
          </div>
          <p v-if="errors.password" class="mt-1 text-xs text-danger">{{ errors.password }}</p>
        </div>

        <!-- Confirm Password Field -->
        <div class="mb-3 lg:mb-4">
          <label class="mb-2 block text-xs font-medium uppercase tracking-wide text-black lg:text-sm"> Confirmar Senha </label>
          <div class="relative">
            <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 lg:pl-4">
              <IcCheck />
            </div>
            <input
              :type="showConfirmPassword ? 'text' : 'password'"
              name="confirmPassword"
              v-model="form.confirmPassword"
              placeholder="Confirme sua senha"
              class="w-full border border-stroke bg-transparent py-2.5 pl-10 pr-10 text-sm text-black outline-none transition-colors focus:border-blue focus:ring-2 focus:ring-blue lg:py-3 lg:pl-12 lg:pr-12 lg:text-base" />
            <button type="button" @click="showConfirmPassword = !showConfirmPassword" class="absolute inset-y-0 right-0 flex items-center pr-3 lg:pr-4">
              <IcEyeOff v-if="showConfirmPassword" />
              <IcEye v-else />
            </button>
          </div>
          <p v-if="errors.confirmPassword" class="mt-1 text-xs text-danger">{{ errors.confirmPassword }}</p>
        </div>

        <!-- Sign Up Button -->
        <div class="mb-3 mt-4 lg:mb-5 lg:mt-6">
          <button
            @click="handleSubmit"
            :disabled="isLoading"
            class="flex w-full cursor-pointer justify-center border border-primary bg-primary p-3 font-medium text-white transition hover:bg-blue disabled:cursor-not-allowed disabled:opacity-50 lg:p-4">
            <div class="flex flex-row items-center text-center">
              <div v-if="isLoading" class="mr-3 animate-spin items-center">
                <svg class="h-5 w-5 text-white lg:h-7 lg:w-7" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
              <span class="flex items-center justify-center text-base font-medium lg:text-lg">
                {{ isLoading ? 'Criando conta...' : 'Criar Conta' }}
              </span>
            </div>
          </button>
        </div>
      </form>
    </template>
  </DefaultAuthLayout>
</template>

<script setup lang="ts">
import DefaultAuthLayout from '@/components/Auths/DefaultAuthLayout.vue'
import { IcBuilding, IcCheck, IcEmail, IcEye, IcEyeOff, IcLock, IcUser } from '@/components/Icons'
import { ICreateUser } from '@/interfaces/entities/user'
import yup from '@/plugins/yup'
import router from '@/router'
import { useSuccessStore } from '@/store/success'
import { useUserStore } from '@/store/user'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { ValidationError } from 'yup'
const { t } = useI18n()
const isLoading = ref(false)
const showPassword = ref(false)
const showConfirmPassword = ref(false)
const { createUser } = useUserStore()

const form = ref({
  email: '',
  name: '',
  password: '',
  confirmPassword: '',
  company: '',
})

const errors = ref({
  email: '',
  name: '',
  password: '',
  confirmPassword: '',
  company: '',
})

const schema = yup.object({
  email: yup.string().required(t('errors.required', { label: 'Email' })),
  password: yup
    .string()
    .required(t('errors.required', { label: 'Senha' }))
    .min(8, t('errors.minLength', { label: 'Senha', min: 8 }))
    .matches(/^(?=.*[a-zA-Z])(?=.*[0-9])/, t('errors.alphanumeric', { label: 'Senha' })),
  name: yup.string().required(t('errors.required', { label: 'Nome' })),
  company: yup.string().required(t('errors.required', { label: 'Empresa' })),
  confirmPassword: yup
    .string()
    .required(t('errors.required', { label: 'Confirmação de senha' }))
    .oneOf([yup.ref('password'), ''], t('errors.passwordMatch')),
})

function handleSubmit() {
  schema
    .validate(form.value, { abortEarly: false })
    .then(() => {
      doSignUp(form.value)
    })
    .catch((err: ValidationError) => {
      setErrors(err)
    })
}

function setErrors(validationErrors: ValidationError) {
  errors.value = {
    email: '',
    name: '',
    password: '',
    confirmPassword: '',
    company: '',
  }

  validationErrors.inner.forEach((error) => {
    if (error.path) {
      errors.value[error.path as keyof typeof errors.value] = error.message
    }
  })
}

const doSignUp = (values: object) => {
  isLoading.value = true

  createUser(values as ICreateUser, {
    onSuccess: () => {
      useSuccessStore().setSuccess(['Conta criada com sucesso'])
      router.push({ name: 'account-activation' })
    },
    onFailure: () => {},
    onCompletion: () => {
      isLoading.value = false
    },
  })
}
</script>
